import type { MaybeRefOrGetter } from "vue";
import { computed, ref, toValue, watch } from "vue";

import { isImage, isPdf } from "@/utils/fileAttachments";

export interface PreviewLayer {
  url: string;
  type: string;
  name?: string;
}

export interface UsePreviewLayersOptions {
  fileUrl: MaybeRefOrGetter<string | undefined>;
  fileType: MaybeRefOrGetter<string | undefined>;
  fileName?: MaybeRefOrGetter<string | undefined>;
}

/**
 * The image or PDF layers to render: the file on screen, plus the next one loading hidden behind it
 * until it reports `loaded`, so a switch, even between an image and a PDF, never shows a skeleton.
 * The first file has nothing to hide behind and shows its own skeleton. `hasFailed` is set while the
 * current file is one that reported `onFailed`.
 */
export function usePreviewLayers(options: UsePreviewLayersOptions) {
  const shownLayer = ref<PreviewLayer>();
  const failedUrl = ref<string>();

  const incomingLayer = computed<PreviewLayer | undefined>(() => {
    const url = toValue(options.fileUrl);
    const type = toValue(options.fileType);
    return url && type && (isImage(type) || isPdf(type)) ? { url, type, name: toValue(options.fileName) } : undefined;
  });

  watch(incomingLayer, (layer) => {
    if (!layer) shownLayer.value = undefined;
  });

  const layers = computed<PreviewLayer[]>(() => {
    const incoming = incomingLayer.value;
    if (!incoming) return [];
    const shown = shownLayer.value;
    return shown && shown.url !== incoming.url ? [shown, incoming] : [incoming];
  });

  const hasFailed = computed(() => !!incomingLayer.value && incomingLayer.value.url === failedUrl.value);

  function isHidden(layer: PreviewLayer): boolean {
    return !!shownLayer.value && layer.url !== shownLayer.value.url;
  }

  function onLoaded(layer: PreviewLayer) {
    if (layer.url === incomingLayer.value?.url) shownLayer.value = layer;
  }

  function onFailed(layer: PreviewLayer) {
    failedUrl.value = layer.url;
    // The file on screen before the failure must not come back behind the next one.
    shownLayer.value = undefined;
  }

  return { layers, hasFailed, isHidden, onLoaded, onFailed };
}
